import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useSaveUserPreferences } from '@/hooks/useUserPreferences'
import { useBranchesForRestaurant } from '@/queries/useBranchesForRestaurant'
import { Building2, MapPin, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { UserRole } from './DashboardContext'

interface Branch {
  id: string
  name: string
  address?: string | null
}

interface Restaurant {
  id: string
  name: string
}

interface DashboardContextSettingsProps {
  role: UserRole
  open: boolean
  onOpenChange: (open: boolean) => void
  // Current settings
  showRestaurant: boolean
  showBranch: boolean
  // Callbacks to update settings
  onShowRestaurantChange: (show: boolean) => void
  onShowBranchChange: (show: boolean) => void
  // Context data
  restaurantId?: string
  restaurantName?: string
  branchId?: string
  branchName?: string
  restaurants?: Restaurant[]
  branches?: Branch[]
  isLoadingRestaurants?: boolean
  isLoadingBranches?: boolean
  onRestaurantChange?: (restaurantId: string | null) => void
  onBranchChange?: (branchId: string | null) => void
}

/**
 * DashboardContextSettings Component
 * 
 * Dialog for configuring the restaurant and branch display in the sidebar.
 * Provides role-based configuration options.
 */
export function DashboardContextSettings({
  role,
  open,
  onOpenChange,
  // Reserved for future use: visibility toggles for restaurant/branch display
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showRestaurant: _showRestaurant,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showBranch: _showBranch,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onShowRestaurantChange: _onShowRestaurantChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onShowBranchChange: _onShowBranchChange,
  restaurantId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  restaurantName: _restaurantName,
  branchId,
  branchName,
  restaurants,
  branches,
  isLoadingRestaurants = false,
  isLoadingBranches = false,
  onRestaurantChange,
  onBranchChange,
}: DashboardContextSettingsProps) {
  const { mutateAsync: saveUserPreferences } = useSaveUserPreferences()

  // Form state - holds pending changes until user clicks Save
  const [formRestaurantId, setFormRestaurantId] = useState<string | undefined>(restaurantId)
  const [formBranchId, setFormBranchId] = useState<string | undefined>(branchId)
  const [isSaving, setIsSaving] = useState(false)

  // Fetch branches for the form's selected restaurant (for super admin, fetch branches for the selected form restaurant)
  const shouldFetchFormBranches = role === 'super_admin' && formRestaurantId && formRestaurantId !== 'all'
  const {
    data: formBranchesData,
    isLoading: isLoadingFormBranches,
  } = useBranchesForRestaurant(shouldFetchFormBranches ? formRestaurantId : null)

  // Determine which branches to show in the dialog
  const dialogBranches = role === 'super_admin' ? (formBranchesData || []) : (branches || [])
  const dialogIsLoadingBranches = role === 'super_admin' ? isLoadingFormBranches : isLoadingBranches

  // Reset form when dialog opens (populate with current values)
  React.useEffect(() => {
    if (open) {
      setFormRestaurantId(restaurantId)
      setFormBranchId(branchId)
    }
  }, [open, restaurantId, branchId])

  // Helper to check if restaurantId is valid
  const isValidRestaurantId = (id?: string): boolean => {
    return id !== undefined && id !== '' && id !== 'loading'
  }

  // Handle restaurant selection in form (no save yet)
  const handleRestaurantSelectChange = (value: string) => {
    const newRestaurantId = value === 'all' ? undefined : value
    setFormRestaurantId(newRestaurantId)
    // Reset branch when restaurant changes
    setFormBranchId(undefined)
  }

  // Handle branch selection in form (no save yet)
  const handleBranchSelectChange = (value: string) => {
    const newBranchId = value === 'all' ? undefined : value
    setFormBranchId(newBranchId)
  }

  // Handle form submission - save to database
  const handleSubmit = async () => {
    console.log('[DashboardContextSettings] Form submitted:', {
      formRestaurantId,
      formBranchId,
      timestamp: new Date().toISOString(),
    })

    setIsSaving(true)
    try {
      // Update parent state first for immediate UI feedback
      if (formRestaurantId !== restaurantId) {
        onRestaurantChange?.(formRestaurantId || null)
      }
      if (formBranchId !== branchId) {
        onBranchChange?.(formBranchId || null)
      }

      // Save to backend via API
      console.log('[DashboardContextSettings] Calling saveUserPreferences...')
      const result = await saveUserPreferences({
        restaurantId: formRestaurantId || undefined,
        branchId: formBranchId || undefined,
      })
      console.log('[DashboardContextSettings] Save successful:', result)

      // Close dialog after successful save
      onOpenChange(false)
    } catch (error) {
      console.error('[DashboardContextSettings] Failed to save:', {
        error,
        errorMessage: error instanceof Error ? error.message : String(error),
        formRestaurantId,
        formBranchId,
      })
      // Don't close dialog on error - let user try again
    } finally {
      setIsSaving(false)
    }
  }

  const handleClose = () => {
    // Reset form to current values when closing without saving
    setFormRestaurantId(restaurantId)
    setFormBranchId(branchId)
    onOpenChange(false)
  }

  const { t } = useTranslation('admin')
  
  // Role-specific description
  const getRoleDescription = () => {
    switch (role) {
      case 'restaurant_admin':
        return t('context.switchDescription.restaurant_admin')
      case 'super_admin':
        return t('context.switchDescription.super_admin')
      default:
        return t('context.switchDescription.default')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-[var(--text-secondary)]" />
            <DialogTitle>{t('context.switchContext')}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {getRoleDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Restaurant Selector - Super Admin only */}
          {role === 'super_admin' && onRestaurantChange && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[var(--text-secondary)]" />
                <Label className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('context.restaurant')}
                </Label>
              </div>
              {isLoadingRestaurants ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={formRestaurantId || 'all'}
                  onValueChange={handleRestaurantSelectChange}
                >
                  <SelectTrigger className="w-full h-10">
                    <SelectValue placeholder={t('context.selectRestaurant')} />
                  </SelectTrigger>
                  <SelectContent className="z-[1100]">
                    <SelectItem value="all">{t('context.allRestaurants')}</SelectItem>
                    {restaurants && restaurants.length > 0 ? (
                      restaurants.map((restaurant) => (
                        <SelectItem key={restaurant.id} value={restaurant.id}>
                          {restaurant.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-restaurants" disabled>
                        {t('context.noRestaurantsAvailable')}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Branch Selector - Restaurant Admin and Super Admin */}
          {(role === 'restaurant_admin' || role === 'super_admin') && onBranchChange && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[var(--text-secondary)]" />
                <Label className="text-sm font-semibold text-[var(--text-primary)]">
                  {t('context.branch')}
                </Label>
              </div>
              {dialogIsLoadingBranches ? (
                <Skeleton className="h-10 w-full" />
              ) : dialogBranches && Array.isArray(dialogBranches) && dialogBranches.length > 0 ? (
                <Select
                  value={formBranchId || 'all'}
                  onValueChange={handleBranchSelectChange}
                  disabled={role === 'super_admin' && (!isValidRestaurantId(formRestaurantId) || formRestaurantId === 'all')}
                >
                  <SelectTrigger className="w-full h-10">
                    <SelectValue placeholder={branchName || t('context.selectBranch')} />
                  </SelectTrigger>
                  <SelectContent className="z-[1100]">
                    {role === 'super_admin' && <SelectItem value="all">{t('context.allBranches')}</SelectItem>}
                    {dialogBranches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                        {branch.address && branch.address !== branch.name ? ` - ${branch.address}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="h-10 w-full flex items-center justify-center text-sm text-[var(--text-secondary)] border border-[var(--border)] rounded-md px-3">
                  {role === 'super_admin' && (!isValidRestaurantId(formRestaurantId) || formRestaurantId === 'all')
                    ? t('context.selectRestaurantFirst')
                    : t('context.noBranchesAvailable')}
                </div>
              )}
            </div>
          )}

        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving}
          >
            {t('context.close')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? t('context.saving', { defaultValue: 'Saving...' }) : t('context.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

